import { TrendingUp, TrendingDown, Lightbulb, BarChart3, Target, PieChart as PieIcon, Activity } from 'lucide-react';
import {
  LineChart, Line, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip,
  PieChart, Pie, Cell, RadialBarChart, RadialBar,
} from 'recharts';
import { useClientSettings } from './settings';

const revenueMix = [
  { name: 'Residential', value: 142000, color: '#6366f1' },
  { name: 'Commercial', value: 88000, color: '#10b981' },
  { name: 'Repairs', value: 38500, color: '#f59e0b' },
  { name: 'Other', value: 16000, color: '#94a3b8' },
];

const cashflowGauge = [
  { name: 'Cashflow health', value: 72, fill: '#10b981' },
];

const revenueTrend = [
  { month: 'Oct', revenue: 22400, profit: 3800 },
  { month: 'Nov', revenue: 24100, profit: 4200 },
  { month: 'Dec', revenue: 28900, profit: 5400 },
  { month: 'Jan', revenue: 21200, profit: 3100 },
  { month: 'Feb', revenue: 25600, profit: 4500 },
  { month: 'Mar', revenue: 27800, profit: 5100 },
];

const expenseBreakdown = [
  { name: 'Wages & Super', amount: 142000 },
  { name: 'Rent', amount: 36000 },
  { name: 'Subscriptions', amount: 18400 },
  { name: 'Motor Vehicle', amount: 12200 },
  { name: 'Marketing', amount: 9800 },
];

const deductionTips = [
  { title: 'Home office expenses', detail: 'You may be eligible to claim a portion of internet, electricity and phone bills if you work from home regularly.' },
  { title: 'Motor vehicle log', detail: 'Keep a 12-week logbook this quarter — it can be used for 5 years and often increases your deduction.' },
  { title: 'Instant asset write-off', detail: 'Equipment purchases under $20,000 may be immediately deductible. Worth reviewing before EOFY.' },
];

const benchmarks = [
  { metric: 'Profit margin', you: 17.0, industry: 12.4, unit: '%' },
  { metric: 'Wages / revenue', you: 49.8, industry: 54.1, unit: '%' },
  { metric: 'Rent / revenue', you: 12.6, industry: 14.8, unit: '%' },
];

export default function InsightsPage() {
  const { t, themeMode } = useClientSettings();
  const isDark = themeMode === 'dark';
  const tooltipBg = isDark ? '#0f172a' : '#ffffff';
  const tooltipBorder = isDark ? '#334155' : '#e2e8f0';
  const axisColor = isDark ? '#94a3b8' : '#64748b';

  return (
    <div className="space-y-5 pt-2">
      <div>
        <h1 className="text-2xl font-bold text-foreground">{t('insights')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">A look at how your business is doing</p>
      </div>

      {/* Card 1: Revenue & profit trend */}
      <div className="rounded-2xl border border-border bg-card p-5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-foreground">{t('revenue_and_profit')}</h2>
            <p className="text-xs text-muted-foreground">Last 6 months</p>
          </div>
          <div className="flex items-center gap-1 rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-700 dark:text-emerald-400">
            <TrendingUp className="h-3 w-3" />
            +12.4%
          </div>
        </div>
        <div className="mt-4 h-44">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={revenueTrend}>
              <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: axisColor }} />
              <YAxis hide />
              <Tooltip
                contentStyle={{ borderRadius: 12, border: `1px solid ${tooltipBorder}`, background: tooltipBg, fontSize: 12 }}
                formatter={(v: number) => `$${v.toLocaleString()}`}
              />
              <Line
                type="monotone"
                dataKey="revenue"
                stroke="#10b981"
                strokeWidth={2.5}
                dot={{ r: 3, fill: '#10b981' }}
                activeDot={{ r: 5 }}
              />
              <Line
                type="monotone"
                dataKey="profit"
                stroke={axisColor}
                strokeWidth={2}
                strokeDasharray="4 4"
                dot={{ r: 2, fill: axisColor }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-3 flex items-center gap-4 text-xs">
          <div className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-emerald-500" />
            <span className="text-muted-foreground">Revenue</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-muted-foreground" />
            <span className="text-muted-foreground">Profit</span>
          </div>
        </div>
      </div>

      {/* Card: Revenue mix donut */}
      <div className="rounded-2xl border border-border bg-card p-5">
        <div className="flex items-center gap-2">
          <PieIcon className="h-4 w-4 text-foreground" />
          <h2 className="text-sm font-semibold text-foreground">Revenue mix</h2>
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">By service line — current FY</p>
        <div className="mt-4 flex items-center gap-4">
          <div className="h-40 w-40 flex-shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={revenueMix}
                  dataKey="value"
                  innerRadius={42}
                  outerRadius={70}
                  paddingAngle={2}
                  stroke="none"
                >
                  {revenueMix.map((d, i) => (
                    <Cell key={i} fill={d.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ borderRadius: 12, border: `1px solid ${tooltipBorder}`, background: tooltipBg, fontSize: 12 }}
                  formatter={(v: number) => `$${v.toLocaleString()}`}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex-1 space-y-2">
            {revenueMix.map(d => {
              const total = revenueMix.reduce((s, x) => s + x.value, 0);
              const pct = Math.round((d.value / total) * 100);
              return (
                <div key={d.name} className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ background: d.color }} />
                  <span className="flex-1 text-xs text-muted-foreground">{d.name}</span>
                  <span className="text-xs font-semibold text-foreground tabular-nums">{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Card: Cashflow health gauge */}
      <div className="rounded-2xl border border-border bg-card p-5">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-foreground" />
          <h2 className="text-sm font-semibold text-foreground">Cashflow health</h2>
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">Based on receivables, payables and 90-day runway</p>
        <div className="mt-2 flex items-center gap-4">
          <div className="relative h-32 w-32 flex-shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <RadialBarChart innerRadius="70%" outerRadius="100%" data={cashflowGauge} startAngle={210} endAngle={-30}>
                <RadialBar background={{ fill: 'hsl(var(--muted))' }} dataKey="value" cornerRadius={20} />
              </RadialBarChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-3xl font-bold text-foreground">72</span>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Healthy</span>
            </div>
          </div>
          <div className="flex-1 space-y-2 text-xs">
            <div className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2">
              <span className="text-muted-foreground">Days of runway</span>
              <span className="font-semibold text-foreground tabular-nums">94</span>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2">
              <span className="text-muted-foreground">Outstanding invoices</span>
              <span className="font-semibold text-foreground tabular-nums">$18,420</span>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2">
              <span className="text-muted-foreground">Bills due 30d</span>
              <span className="font-semibold text-foreground tabular-nums">$9,810</span>
            </div>
          </div>
        </div>
      </div>

      {/* Card 2: Top expenses */}
      <div className="rounded-2xl border border-border bg-card p-5">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-foreground" />
          <h2 className="text-sm font-semibold text-foreground">{t('where_money_goes')}</h2>
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">Top expenses this financial year</p>
        <div className="mt-4 h-48">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={expenseBreakdown} layout="vertical" margin={{ left: 0 }}>
              <XAxis type="number" hide />
              <YAxis
                type="category"
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 11, fill: axisColor }}
                width={110}
              />
              <Tooltip
                contentStyle={{ borderRadius: 12, border: `1px solid ${tooltipBorder}`, background: tooltipBg, fontSize: 12 }}
                formatter={(v: number) => `$${v.toLocaleString()}`}
              />
              <Bar dataKey="amount" fill="hsl(243 75% 59%)" radius={[6, 6, 6, 6]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Card 3: Deduction tips */}
      <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-5">
        <div className="flex items-center gap-2">
          <Lightbulb className="h-4 w-4 text-amber-700 dark:text-amber-400" />
          <h2 className="text-sm font-semibold text-foreground">{t('worth_discussing')}</h2>
        </div>
        <p className="mt-0.5 text-xs text-amber-700 dark:text-amber-400">Possible deductions based on your industry</p>
        <div className="mt-4 space-y-3">
          {deductionTips.map((tip, i) => (
            <div key={i} className="rounded-xl bg-card/80 p-3.5">
              <p className="text-sm font-semibold text-foreground">{tip.title}</p>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{tip.detail}</p>
            </div>
          ))}
        </div>
        <p className="mt-3 text-[11px] italic text-amber-700 dark:text-amber-400">
          Tips are general guidance. Your accountant can confirm what applies to you.
        </p>
      </div>

      {/* Card 4: Benchmarks */}
      <div className="rounded-2xl border border-border bg-card p-5">
        <div className="flex items-center gap-2">
          <Target className="h-4 w-4 text-foreground" />
          <h2 className="text-sm font-semibold text-foreground">{t('how_you_compare')}</h2>
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">Versus similar businesses in your industry</p>
        <div className="mt-4 space-y-4">
          {benchmarks.map(b => {
            const better = b.metric === 'Profit margin' ? b.you > b.industry : b.you < b.industry;
            const youPct = Math.min(100, (b.you / Math.max(b.you, b.industry)) * 100);
            const indPct = Math.min(100, (b.industry / Math.max(b.you, b.industry)) * 100);
            return (
              <div key={b.metric}>
                <div className="mb-1.5 flex items-center justify-between">
                  <span className="text-xs font-medium text-foreground">{b.metric}</span>
                  <span className={`flex items-center gap-1 text-xs font-medium ${better ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                    {better ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                    {better ? 'Above average' : 'Below average'}
                  </span>
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <span className="w-12 text-[10px] font-medium uppercase text-muted-foreground">You</span>
                    <div className="flex-1 overflow-hidden rounded-full bg-muted">
                      <div className="h-2 rounded-full bg-foreground" style={{ width: `${youPct}%` }} />
                    </div>
                    <span className="w-12 text-right text-xs font-semibold tabular-nums text-foreground">{b.you}{b.unit}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-12 text-[10px] font-medium uppercase text-muted-foreground/70">Avg</span>
                    <div className="flex-1 overflow-hidden rounded-full bg-muted">
                      <div className="h-2 rounded-full bg-muted-foreground/50" style={{ width: `${indPct}%` }} />
                    </div>
                    <span className="w-12 text-right text-xs tabular-nums text-muted-foreground">{b.industry}{b.unit}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
